/**
 * API Types for Nour Al-Arabia
 * TypeScript interfaces for API requests, responses, and error handling
 */

// =============================================================================
// User & Authentication Types
// =============================================================================

/** User roles in the system */
export type UserRole = 'STUDENT' | 'TEACHER' | 'ADMIN';

/** Supported locales */
export type Locale = 'ar' | 'en';

/** User information returned from auth API (matches backend UserInfo record) */
export interface UserInfo {
  id: number;
  username: string;
  /** Display name (single field from backend User.name) */
  name: string;
  role: UserRole;
  schoolId: number | null;
  /** School name in Arabic (null for system admins, absent in older API versions) */
  schoolNameAr?: string | null;
  /** School name in English (null for system admins, absent in older API versions) */
  schoolNameEn?: string | null;
  languagePreference: Locale;
}

/** Login request payload */
export interface LoginRequest {
  username: string;
  password: string;
}

/** Login response from server */
export interface LoginResponse {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
  user: UserInfo;
}

/** Token refresh response */
export interface RefreshResponse {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
}

// =============================================================================
// Password Reset & Invitation Types
// =============================================================================

/** Forgot password request payload */
export interface ForgotPasswordRequest {
  emailOrUsername: string;
}

/** Set password request (used for both password reset and invitation acceptance) */
export interface SetPasswordRequest {
  token: string;
  newPassword: string;
}

/** Token validation response */
export interface TokenValidationResponse {
  valid: boolean;
  email?: string;
  purpose?: 'INVITE' | 'RESET';
}

// =============================================================================
// Error Types
// =============================================================================

/** API error response format matching backend */
export interface ApiErrorResponse {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
  details?: Record<string, string>;
}

/** Client-side error wrapper with additional context */
export interface ApiError extends Error {
  status: number;
  code: string;
  details?: Record<string, string>;
  originalError?: unknown;
  /** Entity type for ENTITY_HAS_CHILDREN errors */
  entityType?: 'LEVEL' | 'TIER' | 'SCHOOL';
  /** Child type for ENTITY_HAS_CHILDREN errors */
  childType?: 'BOOK' | 'USER';
  /** Child count for ENTITY_HAS_CHILDREN errors */
  childCount?: number;
}

/** Validation error details */
export interface ValidationError {
  field: string;
  message: string;
}

// =============================================================================
// Generic API Response Types
// =============================================================================

/** Paginated response wrapper */
export interface PaginatedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

/** Pagination request parameters */
export interface PaginationParams {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
}

// =============================================================================
// Type Guards
// =============================================================================

/** Type guard to check if an error is an ApiError */
export function isApiError(error: unknown): error is ApiError {
  return (
    error instanceof Error &&
    'status' in error &&
    'code' in error &&
    typeof (error as ApiError).status === 'number'
  );
}

/** Type guard to check if response is an API error response */
export function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    'message' in data &&
    'timestamp' in data
  );
}
